import React, { createRef, useState } from 'react';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { Spinner } from '../../../components';
import { classNames, clone, delay, formatMiles, fromPhotos, fromStorage } from '../../../utils';
import { ProductsService } from '../../../services';
import { CartIcon, CheckCircleBlueIcon, CheckCircleGreenIcon, CheckCircleOutlinedIcon, ChevronRightIcon, PDFIcon, WaitingPeriod } from '../../../assets/icons';

class ProductDetails extends React.Component {

  state = {
    isLoading: false,
    product: null,
    prices: [],
    deductibles: [],
    selectedPriceId: '',
    selectedDeductible: null,
  }

  confirmModalRef = createRef();
  goBack = createRef();

  componentDidMount() {
    this.goBack.current = document.getElementById('header-goback');
    this.goBack.current.classList.remove('hidden');

    this.getProduct();
  }

  componentWillUnmount() {
    this.goBack.current.classList.add('hidden');
  }

  getProduct = async () => {
		const id = atob(atob(this.props?.match?.params?.idBase64));
    if (isNaN(id)) return toast.error('No se pudo encontrar el producto');

    try {
      this.setState({ isLoading: true });
      const product = await ProductsService.getProduct(+id);
      await this.setState({ product });
      await delay(10);
      this.parseProduct();

    } catch (error) {
      console.log(error);
    }
    this.setState({ isLoading: false });
  }

  parseProduct = () => {
    const breadcrumb = document.getElementById('breadcrumb-content');
    breadcrumb.innerHTML = this.state.product?.name || '';

    const inCartProduct = this.isInCart();

    if (this.state.product?.is_deductible_based) {
      this.parseDeductibles();
    } else {
      this.setState({
        selectedPriceId: inCartProduct?.selectedPriceId || '',
        product: {
          ...this.state.product,
          prices: this.state.product?.prices,
        },
      });
    }
  }

  parseDeductibles = () => {
    const { product } = this.state;

    // Si el producto es en base a deducibles, calcula cuántas columnas tiene
    const maxColumns = Math.max(...product?.deductibles?.map(d => {
      // Busca la primera fila de cada deducible para obtener la cantidad de columnas
      return d.premiums.filter(p => p.row_order === 1)?.length;
    }));

    const deductibles = [];
    const columns = Array(maxColumns).fill(0).map((_, i) => i + 1);

    // Se realiza la búsqueda en base a la cantidad máxima de columnas
    columns.forEach(colOrder => {
      let dedPremiums = [];
      // por cada columna va la suma asegurada, el nombre del deducible, las primas de cada columna
      product?.deductibles?.forEach(ded => {
        const colPremiums = ded.premiums
          .filter(p => p.col_order === colOrder && !p.is_disabled)
          .map(p => ({ ...p, deducibleName: ded.name }));

        dedPremiums = dedPremiums.concat(colPremiums);
      });

      if (!dedPremiums.length) return;

      deductibles.push({
        col_order: colOrder,
        insured_sum: dedPremiums[0].insured_sum,
        premiums: dedPremiums,
      });
    });

    this.setState({ deductibles: deductibles });
  }

  showMore = () => {
    const el = document.createElement('a');
    el.href = fromStorage(this.state.product?.details_file);
    el.download = this.state.product?.name;
    el.target = '_blank';
    el.click();
    el.remove();
  }

  isInCart = () => {
    const { product } = this.state;
    return this.props.cart.find(x => x.id === product?.id);
  }

  onAddToCart = () => {
    this.props.dispatch({ type: 'SET_REQUEST', payload: null });

    if (this.isInCart()) {
      this.removefromCart();
      this.setState({ selectedPriceId: null, selectedDeductible: null });

    } else {
      if (!this.isValidForm()) return;
      this.confirmModalRef.current.showModal();
      this.addToCart();
    }
  }

  addToCart = () => {
    const { product } = this.state;
    const products = [
      ...this.props.cart,
      {
        ...product,
        selectedPriceId: this.state.selectedPriceId,
        selectedDeductible: this.state.selectedDeductible,
      },
    ];
    this.props.dispatch({ type: 'SET_CART', payload: products });
  }

  onChange = async (value, target) => {
    const products = clone(this.props.cart);
    const index = products.findIndex(x => x.id === this.state.product?.id);
    products[index][target] = value;

    this.props.dispatch({ type: 'SET_CART', payload: products });
    this.props.dispatch({ type: 'SET_REQUEST', payload: null });
  }

  removefromCart = () => {
    const { product } = this.state;
    const others = this.props.cart.filter(p => p.id !== product?.id);
    this.props.dispatch({ type: 'SET_CART', payload: others });
  }

  goToCart = () => {
    this.confirmModalRef.current.close();
    const modal = document.getElementById('cart-modal');
    modal?.showModal();
  }

  isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    };

    const { product, selectedDeductible, selectedPriceId } = this.state;

    if (product?.is_deductible_based) {
      if (!selectedDeductible) return onError('Debe seleccionar la cobertura');
    } else {
      if (!selectedPriceId) return onError('Debe seleccionar el precio');
    }

    return true;
  }

  onSelectDeductible = async (selectedItem) => {
    if (!selectedItem) return toast.error('Debe seleccionar alguna opción de deducible');

    if (this.isInCart()) {
      if (selectedItem.isSelected) {
        this.onAddToCart();
      } else {
        // Quita el anterior del carrito
        this.onAddToCart();

        // Establece el nuevo valor en el carrito
        await this.setState({ selectedDeductible: selectedItem });
        delay(1);
        this.onAddToCart();
      }
    } else {
      // Si el producto es RCV y tiene el carrito lleno, se le notifica
      if (this.state.product?.is_rcv && this.props.cart.length)
        return toast.error('Debe tener el carrito vacío para agregar este producto');
      // Si es algún otro producto y tiene un RCV en el carrito, se le notifica
      if (!this.state.product?.is_rcv && this.props.cart.some(x => x.is_rcv))
        return toast.error('Usted tiene un RCV en el carrito, debe quitarlo para poder agregar este producto');

      await this.setState({ selectedDeductible: selectedItem });
      delay(1);
      this.onAddToCart();
    }
  }

  render() {

    const { deductibles, isLoading, product } = this.state;
    const cartProduct = this.isInCart();
    const isInCart = !!cartProduct;

    if (isLoading) return <LocalSpinner />;
    if (!product) return <NotFoundProduct />;

    return (
      <>
        <dialog ref={this.confirmModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent">
          <div className="w-full sm:w-[30rem] bg-white rounded-xl overflow-hidden">
            <div className="py-8 px-5">
              <p className="text-center text-lg">¿Desea ver y agregar más servicios?</p>
            </div>

            <div className="flex justify-between h-12 font-semibold">
              <button
                className="flex-1 text-sm bg-white p-3"
                onClick={() => this.confirmModalRef.current.close()}
              >
                <span>Continuar Comprando</span>
              </button>

              <button
                className="flex-1 text-white bg-blue"
                onClick={this.goToCart}
              >
                <span>Ir al Carrito</span>
              </button>
            </div>
          </div>
        </dialog>

        <div className="flex flex-col items-center mt-8 lg:mt-0">
          <div className="flex flex-col items-center justify-center">
            <div className="flex self-center overflow-hidden max-w-[50rem] max-h-52 rounded-2xl">
              {!!product?.central_icon && (
                <img src={fromPhotos(product?.central_icon)} className="object-cover object-center" alt="" />
              )}
            </div>
          </div>

          <p className="text-text py-4">
            { product?.description }
          </p>

          <div className="flex justify-center">
            <div className="flex bg-darkBlue rounded-xl p-2 gap-2 items-center text-white max-w-[30rem]">
              <div className="bg-[#C2F2FF] w-12 h-12 rounded-full flex items-center justify-center aspect-square">
                <img src={WaitingPeriod} className="w-6 h-6 object-contain" alt="" />
              </div>
              <div>
                <p className="font-semibold">Plazo de espera para activación</p>
                <p className="text-sm">{ product?.activation_period }</p>
              </div>
            </div>
          </div>

          {!!product?.services?.length && (
            <div className="my-8">
              <p className="text-2xl font-bold text-darkBlue text-center">¿Qué ofrece?</p>
              <div className="max-w-[800px] flex flex-wrap justify-center gap-4 mt-4">
                {product?.services?.map((item, index) => (
                  <OfferItem
                    key={index}
                    photo={fromPhotos(item.photo)}
                    description={item?.service?.name}
                  />
                ))}
              </div>
            </div>
          )}

          {!!product?.details_file && (
            <div className="flex justify-center my-5">
              <button
                className="flex items-center gap-2"
                onClick={this.showMore}
              >
                <img src={PDFIcon} className="w-8 h-8 object-contain" alt="" />
                <span className="text-lg font-bold text-darkBlue border-b-2 border-b-darkBlue">Conocer más</span>
              </button>
            </div>
          )}

          <div className="bg-gray5 rounded-2xl max-w-full">
            <div className="flex justify-start items-start gap-4 p-4 overflow-x-auto">
              {product?.is_deductible_based ? (
                <>
                  {deductibles.map((item, index) => {
                    const isSelectedCol = cartProduct?.selectedDeductible?.col_order === item.col_order;

                    return (
                      <div key={index}>
                        <div className="bg-white rounded-2xl w-[20rem] overflow-hidden shadow-md">
                          <div className="w-full flex justify-center items-center text-darkBlue font-bold my-2">
                            <img src={CheckCircleGreenIcon} className="w-4 h-4 object-contain mr-1" alt="" />
                            <p>Cobertura: ${ formatMiles(item.insured_sum, 2, '').trim() }</p>
                          </div>
                          <p className="text-xs text-center pb-4 border-b border-b-gray6">
                            Seleccione una de las siguientes opciones de deducible
                          </p>
                          {product?.deductibles?.map((ded, dedIdx) => {
                            const isSelected = isInCart
                              ? cartProduct?.selectedDeductible?.deductible_id === ded.id && cartProduct?.selectedDeductible?.col_order === item.col_order
                              : this.state.selectedDeductible?.deductible_id === ded.id && this.state.selectedDeductible?.col_order === item.col_order;

                            const premiums = item.premiums.filter(x => x.deductible_id === ded.id);
                            if (!premiums.length) return null;

                            return (
                              <DeductibleItem
                                key={`ded-${dedIdx}`}
                                name={ded.name}
                                premiums={premiums}
                                isSelected={isSelected}
                                onSelect={this.onSelectDeductible}
                              />
                            );
                          })}

                          <div className="flex justify-end">
                            <button
                              className={classNames(
                                'w-full h-10 m-2 py-2 px-5 rounded-lg flex justify-between items-center',
                                isSelectedCol ? 'bg-green' : 'bg-darkBlue'
                              )}
                              onClick={() => {
                                isSelectedCol
                                  ? this.onAddToCart()
                                  : toast.error('Debe seleccionar el deducible');
                              }}
                            >
                              {isSelectedCol
                                ? <img src={CheckCircleOutlinedIcon} className="w-5 h-5 object-contain brightness-0 invert" alt="" />
                                : <img src={CartIcon} className="w-5 h-5 object-contain" alt="" />
                              }
                              <span className="text-center text-white font-semibold">
                                {isSelectedCol ? 'Agregado' : 'Agregar al carrito'}
                              </span>
                              <div className="w-5 h-5 object-contain" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  {(product?.prices ?? []).map((item, index) => {
                    // const minPrice = !!parseFloat(item.price_monthly) ? item.price_monthly : item.price_annual;
                    const isSelected = (isInCart ? cartProduct?.selectedPriceId : this.state.selectedPriceId) === item.id;
                    const hasMonthlyPrice = !!parseFloat(item.price_monthly);

                    return (
                      <div key={index} className="bg-white rounded-2xl min-w-[20rem] w-[20rem]">
                        <div className="w-full">
                          <p className="bg-[#C2F2FF] px-4 py-3 rounded-2xl text-darkBlue font-bold">
                            { item.name }
                          </p>
                          {hasMonthlyPrice && (
                            <>
                              <p className="text-darkBlue font-bold text-center text-lg mt-1">
                                { formatMiles(item.price_monthly) }
                              </p>
                              <p className="text-blue font-bold text-center text-lg">Prima Mensual</p>
                            </>
                          )}
                          {/* {product?.fees > 1 && (
                            <span>
                              { `${product?.fees} cuota${product?.fees > 1 ? 's' : ''}` }
                            </span>
                          )} */}
                        </div>

                        <div className="p-3 w-full">
                          {item?.additional_props?.map((prop, propIdx) => (
                            <div key={propIdx} className="flex items-center mt-2 gap-2 text-sm">
                              <img src={CheckCircleBlueIcon} className="w-4 h-4 object-contain" alt="" />
                              <p>{ prop?.name } { prop.insured_sum ? formatMiles(prop.insured_sum) : '' }</p>
                            </div>
                          ))}

                          {(!!item?.insurability_age && item?.insurability_age !== '0-0') && (
                            <div className="flex items-center mt-2 gap-2 text-sm">
                              <img src={CheckCircleBlueIcon} className="w-4 h-4 object-contain" alt="" />
                              <p className="text-sm">
                                Edad de Asegurabilidad { item.insurability_age } años
                              </p>
                            </div>
                          )}

                          {!!item?.deductible && (
                            <div className="flex items-center mt-2 gap-2 text-sm">
                              <img src={CheckCircleBlueIcon} className="w-4 h-4 object-contain" alt="" />
                              <p className="text-sm">Deducible { item.deductible }</p>
                            </div>
                          )}

                          {!!item?.insured_sum && (
                            <div className="w-full flex justify-center items-center text-darkBlue font-bold my-3">
                              <img src={CheckCircleGreenIcon} className="w-4 h-4 object-contain mr-1" alt="" />
                              <p>Suma Asegurada { formatMiles(item.insured_sum) }</p>
                            </div>
                          )}

                          {item.price_annual > 0 && (
                            <p className="text-text font-bold text-center text-lg my-5">
                              Prima Anual { formatMiles(item.price_annual) }
                            </p>
                          )}

                          <button
                            className={classNames('w-full h-10 py-2 px-5 rounded-lg flex justify-between items-center mt-2', isSelected ? 'bg-green' : 'bg-darkBlue')}
                            onClick={async () => {
                              if (isInCart) {
                                isSelected ? this.onAddToCart() : this.onChange(item.id, 'selectedPriceId');
                              } else {
                                // Si el producto es RCV y tiene el carrito lleno, se le notifica
                                if (this.state.product?.is_rcv && this.props.cart.length)
                                  return toast.error('Debe tener el carrito vacío para agregar este producto');
                                // Si es algún otro producto y tiene un RCV en el carrito, se le notifica
                                if (!this.state.product?.is_rcv && this.props.cart.some(x => x.is_rcv))
                                  return toast.error('Usted tiene un RCV en el carrito, debe quitarlo para poder agregar este producto');

                                await this.setState({ selectedPriceId: item.id });
                                delay(1);
                                this.onAddToCart();
                              }
                            }}
                          >
                            {isSelected
                              ? <img src={CheckCircleOutlinedIcon} className="w-5 h-5 object-contain brightness-0 invert" alt="" />
                              : <img src={CartIcon} className="w-5 h-5 object-contain" alt="" />
                            }
                            <span className="text-center text-white font-semibold">
                              {isSelected ? 'Agregado' : 'Agregar al carrito'}
                            </span>
                            <div className="w-5 h-5 object-contain" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

const LocalSpinner = () => (
  <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
    <Spinner />
  </div>
);

const NotFoundProduct = () => (
  <div style={{ height: 'calc(100vh - 6rem)' }} className="flex justify-center items-center">
    <p className="text-center text-lg font-semibold text-text">No se encontró el producto</p>
  </div>
);

const OfferItem = ({ photo, description }) => (
  <div className="flex items-center w-[188px]">
    <div className="w-10 h-10 overflow-hidden justify-center items-center rounded-lg mr-2">
      <img src={photo} className="w-10 h-10 object-cover object-center" alt="" />
    </div>
    <p className="text-sm font-semibold flex-1">{ description }</p>
  </div>
);

const DeductibleItem = ({ isSelected, name, onSelect, premiums }) => {

  const [isShown, setIsShown] = useState(false);

  const _onSelect = (e) => {
    e.stopPropagation();
    onSelect({
      col_order: premiums[0].col_order,
      deductible_id: premiums[0].deductible_id,
      isSelected,
    });
  };

  return (
    <>
      <div className="flex items-center w-full border-b border-b-gray6 cursor-pointer" onClick={() => setIsShown(!isShown)}>
        <button onClick={_onSelect}>
          <div
            className={classNames(
              'w-5 h-5 rounded-full border m-2 transition-all',
              isSelected ? 'bg-green border-green' : 'bg-[#EAEAEA] border-gray6'
            )}
          />
        </button>
        <div className="flex-1">
          <p className="text-sm font-semibold">{ name }</p>
        </div>
        <div
          className={classNames(
            'w-5 h-5 rounded-full border m-2 flex justify-center items-center transition-all',
            isShown ? 'bg-darkBlue border-darkBlue' : 'border-gray6'
          )}
        >
          <img
            src={ChevronRightIcon}
            className={classNames(
              'w-2.5 h-2.5 object-contain transition-all',
              isShown ? '-rotate-90 brightness-0 invert' : 'rotate-90'
            )}
            alt=""
          />
        </div>
      </div>

      {isShown && (
        <div className="px-0 w-full border-b border-b-gray6">
          <div className="bg-darkBlue px-2 py-2 justify-between grid grid-cols-3 text-white text-xs [&>p]:m-0">
            <p>Edad</p>
            <p>Mensual</p>
            <p className="text-right">Anual</p>
          </div>
          {premiums?.map((premium, preIdx) => (
            <div key={`pre-${preIdx}`} className="grid grid-cols-3 px-2 py-1.5 border-b border-b-gray6">
              <p className="text-sm font-semibold text-darkBlue">
                { premium.insurability_age.replace('-',' - ') }
              </p>
              <p className="text-sm font-semibold text-text">
                { formatMiles(premium.price_monthly) }
              </p>
              <p className="text-sm font-semibold text-text text-right">
                { formatMiles(premium.price_annual) }
              </p>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default connect(state => ({
	cart: state.cart,
  request: state.request,
}))(ProductDetails);
